import React, { useEffect, useState } from "react";
import { Tabs, Modal, Switch, TabsProps } from "antd";
import {
  CheckOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { HttpError, useList } from "@refinedev/core";

import { QuestionsContent } from "./tabs/questions";
import {
  blankQuestion,
  default_section,
  default_statement,
  default_topic,
  questionTabName,
  selectionTabName,
} from "./tabs/constants";
import { SelectionContent } from "./tabs/selection";
import {
  Question,
  ISection as Section,
  SectionOption,
  SelectedPart,
  ITopic as Topic,
  TopicOption,
  InputStatus,
  IStatement as Statement,
  StatementOption,
} from "interfaces";

import "./group.css";
import { useLocation, useNavigate } from "react-router-dom";

const { confirm } = Modal;

type QuestionGroupProps = {
  syllabus_id: string | null;
  questions: Question[];
  selectedParts: SelectedPart[];
  memoSelectedParts: SelectedPart[];
  explanationSelectedParts: any;
  selectionOpen: boolean;
  percentComplete: number;
  paper: string;
  editMode: boolean;
  currentPaperKey: string;
  updateQuestions: (questions: Question[]) => void;
  setSelectionOpen: (open: boolean) => void;
  setSelectedParts: (parts: SelectedPart[]) => void;
  setMemoSelectedParts: (parts: SelectedPart[]) => void;
  setExplanationSelectedParts: (parts: any) => void;
  handleInsertQuestion: (question: Question) => Promise<number>;
  handleUpdateQuestion: (question: Question) => void;
  handleDeleteQuestion: (questionId: number) => void;
  handleRemoveExplanation: (questionId: number) => void;
  handleFindUnusedParts: () => void;
  handleFinish: () => void;
  setEditMode: (checked: boolean) => void;
  setCurrentPaperKey: (key: string) => void;
};

type QuestionDataProps = {
  newQuestionId?: number;
  newSelectedParts?: SelectedPart[];
  newSelectedMemoParts?: SelectedPart[];
  newSelectedExplanationParts?: any;
  newSection?: Section;
  newTopic?: Topic;
  newPointsPossible?: number;
  newStatements?: Statement[];
  newQuestionName?: string;
  newMCAnswer?: string;
  newState?: string;
  newType?: string;
  newAIGroup?: boolean;
};

export const QuestionGroup: React.FC<QuestionGroupProps> = ({
  syllabus_id,
  questions,
  selectedParts,
  memoSelectedParts,
  explanationSelectedParts,
  selectionOpen,
  percentComplete,
  paper,
  editMode,
  currentPaperKey,
  handleInsertQuestion,
  handleUpdateQuestion,
  handleDeleteQuestion,
  handleRemoveExplanation,
  updateQuestions,
  setSelectionOpen,
  setSelectedParts,
  setMemoSelectedParts,
  setExplanationSelectedParts,
  handleFindUnusedParts,
  handleFinish,
  setEditMode,
  setCurrentPaperKey,
}) => {
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(questionTabName);
  const [currentQuestionId, setCurrentQuestionId] = useState<number>(-1);
  const [questionName, setQuestionName] = useState("");
  const [pointsPossible, setPointsPossible] = useState(0);
  const [mcAnswer, setMCAnswer] = useState("");
  const [section, setSection] = useState<Section>(default_section);
  const [topic, setTopic] = useState<Topic>(default_topic);
  const [selectedStatements, setSelectedStatements] = useState<Statement[]>([]);

  const [questionNameStatus, setQuestionNameStatus] = useState<InputStatus>("");
  const [pointsPossibleStatus, setPointsPossibleStatus] =
    useState<InputStatus>("");
  const [mcAnswerStatus, setMCAnswerStatus] = useState<InputStatus>("");
  const [sectionStatus, setSectionStatus] = useState<InputStatus>("");
  const [topicStatus, setTopicStatus] = useState<InputStatus>("");
  const [statementStatus, setStatementStatus] = useState<InputStatus>("");
  const [filteredTopics, setFilteredTopics] = useState<Topic[]>([]);
  const [filteredStatements, setFilteredStatements] = useState<Statement[]>([]);

  const [sectionInputValue, setSectionInputValue] = useState<string>("");
  const [topicInputValue, setTopicInputValue] = useState<string>("");
  const [statementInputValue, setStatementInputValue] = useState<string>("");

  const [questionState, setQuestionState] = useState<string>("Usable");
  const [questionType, setQuestionType] = useState<string>("SQ");
  const [aiGroup, setAIGroup] = useState<boolean>(false);

  const { data: section_data } = useList<Section, HttpError>({
    resource: `pdf/${paper}/sections?syllabus_id=${syllabus_id}`,
  });

  const { data: topic_data } = useList<Topic, HttpError>({
    resource: `pdf/${paper}/topics?syllabus_id=${syllabus_id}`,
  });

  const { data: statement_data } = useList<Statement, HttpError>({
    resource: `pdf/${paper}/statements?syllabus_id=${syllabus_id}`,
  });

  const sections = section_data?.data ?? [];
  const topics = topic_data?.data ?? [];
  const statements = statement_data?.data ?? [];

  useEffect(() => {
    setFilteredTopics(topics);
  }, [topics]);

  useEffect(() => {
    setFilteredStatements(statements);
  }, [statements]);

  const navigate = useNavigate();
  useEffect(() => {
    if (!questions) return;
    const queryParams = new URLSearchParams(location.search);
    const questionId = queryParams.get("question");
    if (!questionId) return;
    const queryQuestion = questions.find((q) => q.id === parseInt(questionId));
    if (queryQuestion) {
      selectQuestion(queryQuestion);

      const params = new URLSearchParams(location.search);
      params.delete("question");

      navigate(
        {
          pathname: location.pathname,
          search: params.toString(),
        },
        { replace: true }
      );
    }
  }, [questions]);

  useEffect(() => {
    if (selectionOpen) {
      setActiveTab(selectionTabName);
    }
  }, [selectionOpen]);

  const newQuestion = () => {
    setActiveTab(selectionTabName);
  };

  const editQuestion = (question: Question) => {
    const newQuestionId = question.id;
    const newSelectedParts = question.parts;
    const newSelectedMemoParts = question.memo_parts;
    const newSelectedExplanationParts = question.explanation;
    const newPointsPossible = question.points_possible;
    const newSection = question.section;
    const newTopic = question.topic;
    const newStatements = question.statements;
    const newQuestionName = question.title;
    const newState = question.status;
    const newType = question.type;
    const newMCAnswer = question.mc_answer;
    const newAIGroup = question.ai_group;
    setCurrentQuestionData({
      newQuestionId,
      newSelectedParts,
      newSelectedMemoParts,
      newSelectedExplanationParts,
      newPointsPossible,
      newSection,
      newTopic,
      newStatements,
      newQuestionName,
      newState,
      newType,
      newMCAnswer,
      newAIGroup,
    });
    setActiveTab(selectionTabName);
  };

  const selectQuestion = (question: Question) => {
    editQuestion(question);
  };

  const setCurrentQuestionData = (props: QuestionDataProps | null = null) => {
    const {
      newQuestionId = -1,
      newSelectedParts = [],
      newSelectedMemoParts = [],
      newSelectedExplanationParts,
      newSection = default_section,
      newTopic = default_topic,
      newPointsPossible = 0,
      newStatements = [],
      newQuestionName = "",
      newMCAnswer = "",
      newState = "Usable",
      newType = "SQ",
      newAIGroup = false,
    } = (props ?? {}) as QuestionDataProps;

    setCurrentQuestionId(newQuestionId);
    setSelectedParts(newSelectedParts);
    setMemoSelectedParts(newSelectedMemoParts);
    setExplanationSelectedParts(newSelectedExplanationParts);
    setQuestionName(newQuestionName);
    setMCAnswer(newMCAnswer);
    setPointsPossible(newPointsPossible);
    setSection(newSection);
    setSectionInputValue(newSection.name);
    setTopic(newTopic);
    setSelectedStatements(newStatements);
    setTopicInputValue(newTopic.name);
    setFilteredTopics(
      newSection.id !== -1
        ? topics.filter((topic) => topic.section_id === newSection.id)
        : topics
    );
    setFilteredStatements(
      newTopic.id !== -1
        ? statements.filter((statement) => statement.topic_id === newTopic.id)
        : statements
    );
    setQuestionState(newState);
    setQuestionType(newType);
    setAIGroup(newAIGroup);
  };

  const onTabChange = (key: string) => {
    setActiveTab(key);
    setSelectionOpen(key === selectionTabName);
    if (key === questionTabName) {
      setCurrentQuestionData(null);
    }
  };

  const showConfirm = () => {
    const isComplete = percentComplete >= 100;
    if (isComplete) {
      confirm({
        title: "Confirm finish",
        icon: <ExclamationCircleOutlined />,
        content: "Are you sure you want to finish?",
        onOk: () => handleFinish(),
        onCancel: () => {},
      });
    } else {
      confirm({
        title: `Current progress: ${percentComplete}%`,
        icon: <ExclamationCircleOutlined />,
        content: "You must complete all questions before finishing!",
        onOk: () => {},
        onCancel: () => {},
      });
    }
  };

  const handleQuestionNameChange = (value: string) => {
    setQuestionNameStatus("");
    setQuestionName(value);
  };

  const handlePointsPossibleChange = (value: number | null) => {
    if (value === null) return;
    setPointsPossibleStatus("");
    setPointsPossible(value);
  };

  const handleMCAnswerChange = (value: string) => {
    setMCAnswerStatus("");
    setMCAnswer(value);
  };

  const handleSelectSection = (option: SectionOption) => {
    const newTopics = topics.filter(
      (topic) => topic.section_id === option.value
    );
    setFilteredTopics(newTopics);
    setFilteredStatements(
      statements.filter((statement) =>
        newTopics.find((t) => t.id === statement.topic_id)
      )
    );
    setSectionStatus("");
    setSection(
      sections.find((section) => section.id === option.value) ?? default_section
    );
    setSectionInputValue(option.label);
  };

  const handleOnSectionChange = (value: string) => {
    if (value === "") {
      setFilteredTopics(topics);
      setFilteredStatements(statements);
      setSection(default_section);
      setSectionInputValue("");
    }
    setTopicInputValue("");
    setTopic(default_topic);
    setStatementInputValue("");
    setSelectedStatements([]);
  };

  const handleOnTopicSelect = (option: TopicOption) => {
    if (section.id !== option.section_id) {
      const sectionFromTopic =
        sections.find((section) => section.id === option.section_id) ??
        default_section;
      setSection(sectionFromTopic);
      setSectionInputValue(sectionFromTopic.name);
      setSectionStatus("");

      const newTopics = topics.filter(
        (topic) => topic.section_id === sectionFromTopic.id
      );
      setFilteredTopics(newTopics);
      setFilteredStatements(
        statements.filter((statement) =>
          newTopics.find((t) => t.id === statement.topic_id)
        )
      );
    }
    setTopicStatus("");
    setStatementStatus("");
    setTopic(
      topics.find((topic) => topic.id === option.value) ?? default_topic
    );
    setTopicInputValue(option.label);
  };

  const handleOnTopicChange = (value: string) => {
    if (value === "") {
      const newTopics = topics.filter(
        (topic) => topic.section_id === section.id
      );
      setFilteredStatements(
        statements.filter((statement) =>
          newTopics.find((t) => t.id === statement.topic_id)
        )
      );
      setTopic(default_topic);
      setTopicInputValue("");
    }
    setStatementInputValue("");
    setSelectedStatements([]);
  };

  const handleOnStatementSelect = (
    option: StatementOption | StatementOption[]
  ) => {
    console.log(option);
    const options = Array.isArray(option) ? option : [option];
    if (options.length === 0) {
      setSelectedStatements([]);
      return;
    }

    const firstOption = options[0];

    const topicFromStatement =
      topics.find((t) => t.id === firstOption.topic_id) ?? default_topic;
    const sectionFromTopic =
      sections.find((s) => s.id === topicFromStatement.section_id) ??
      default_section;
    if (topic.id !== topicFromStatement.id) {
      setTopic(topicFromStatement);
      setTopicInputValue(topicFromStatement.name);
      setTopicStatus("");

      setSection(sectionFromTopic);
      setSectionInputValue(sectionFromTopic.name);
      setSectionStatus("");

      setFilteredStatements(
        statements.filter((s) => s.topic_id === topicFromStatement.id)
      );
    }

    const selectedStatements = statements.filter((statement) =>
      options.some(
        (opt) => `${statement.topic_id}-${statement.id}` === opt.value
      )
    );
    console.log("selectedStatements", selectedStatements);
    setSelectedStatements(selectedStatements);
  };

  const updateStatus = (
    newQuestionNameStatus: InputStatus,
    newSectionStatus: InputStatus,
    newTopicStatus: InputStatus,
    newMCAnswerStatus: InputStatus
  ) => {
    setQuestionNameStatus(newQuestionNameStatus);
    setSectionStatus(newSectionStatus);
    setTopicStatus(newTopicStatus);
    setMCAnswerStatus(newMCAnswerStatus);
  };

  const modifyQuestion = (newQuestions: Question[], tabName: string) => {
    updateQuestions(newQuestions);
    setCurrentQuestionData(null);
    setActiveTab(tabName);
  };

  const handleEditModeChange = (checked: boolean) => {
    setEditMode(checked);
    if (checked && activeTab === selectionTabName) {
      setActiveTab(questionTabName);
      setSelectionOpen(false);
      setSelectedParts([]);
      setMemoSelectedParts([]);
    }
  };

  const tabItems: TabsProps["items"] = [
    {
      key: "questions",
      label: "Questions",
      children: (
        <QuestionsContent
          questions={questions}
          blankQuestion={blankQuestion}
          percentComplete={percentComplete}
          editMode={editMode}
          newQuestion={newQuestion}
          selectQuestion={selectQuestion}
          editQuestion={editQuestion}
          showConfirm={showConfirm}
          showUnusedParts={handleFindUnusedParts}
        />
      ),
    },
    {
      key: "selection",
      label: "Selection",
      children: (
        <SelectionContent
          questions={questions}
          currentQuestionId={currentQuestionId}
          section={section}
          topic={topic}
          statements={selectedStatements}
          questionNameStatus={questionNameStatus}
          questionName={questionName}
          pointsPossible={pointsPossible}
          pointsPossibleStatus={pointsPossibleStatus}
          mcAnswerStatus={mcAnswerStatus}
          mcAnswer={mcAnswer}
          sectionStatus={sectionStatus}
          topicStatus={topicStatus}
          statementStatus={statementStatus}
          sections={sections}
          filteredTopics={filteredTopics}
          filteredStatements={filteredStatements}
          sectionInputValue={sectionInputValue}
          topicInputValue={topicInputValue}
          statementInputValue={statementInputValue}
          selectedParts={selectedParts}
          memoSelectedParts={memoSelectedParts}
          explanationSelectedParts={explanationSelectedParts}
          questionState={questionState}
          questionType={questionType}
          aiGroup={aiGroup}
          currentPaperKey={currentPaperKey}
          handleQuestionNameChange={handleQuestionNameChange}
          handlePointsPossibleChange={handlePointsPossibleChange}
          handleMCAnswerChange={handleMCAnswerChange}
          handleSelectSection={handleSelectSection}
          handleOnSectionChange={handleOnSectionChange}
          handleOnTopicChange={handleOnTopicChange}
          handleOnTopicSelect={handleOnTopicSelect}
          handleOnStatementSelect={handleOnStatementSelect}
          setSectionInputValue={setSectionInputValue}
          setTopicInputValue={setTopicInputValue}
          setStatementInputValue={setStatementInputValue}
          setQuestionState={setQuestionState}
          setQuestionType={setQuestionType}
          setAIGroup={setAIGroup}
          updateStatus={updateStatus}
          insertQuestionApi={handleInsertQuestion}
          updateQuestionApi={handleUpdateQuestion}
          deleteQuestionApi={handleDeleteQuestion}
          removeExplanationApi={handleRemoveExplanation}
          modifyQuestion={modifyQuestion}
          setCurrentPaperKey={setCurrentPaperKey}
        />
      ),
    },
  ];

  return (
    <div className="container">
      <div className="switch-container">
        <Switch
          className="switch"
          checkedChildren={<CheckOutlined />}
          unCheckedChildren={<EditOutlined />}
          checked={editMode}
          onChange={handleEditModeChange}
        />
      </div>
      <Tabs activeKey={activeTab} items={tabItems} onChange={onTabChange} />
    </div>
  );
};
